import * as React from 'react'
import { useTranslation } from 'translations'
import { ArticleService, Locale } from 'admin/articleService'
import { ImageUploadResponse } from 'http/imagesService'
import { MessageKind } from 'uiComponents/messages'
import { SingleSelectOption, Toggle } from 'uiComponents/input'
import { DisabledItemHoverInfotip } from 'uiComponents/infotip'
import { StatusChip } from 'uiComponents/statusChip/statusChip'
import { GoogleListingItem, TicketCandidate } from 'channels/googleTTDService'
import { useImageSize } from '../../hooks/useImageSize'
import { useTicketValidity } from '../../hooks/useTicketValidity'
import { isReady, readinessChecks, ReadinessKey } from '../../model/readiness'
import { ReadinessChecklist } from '../readinessChecklist/readinessChecklist'
import { VenueSection } from '../venueSection/venueSection'
import { ListedTicket, TicketsTable } from '../ticketsTable/ticketsTable'
import './listingCard.scss'

interface ListingCardProps {
    accountSlug: string
    articleService: ArticleService
    item: GoogleListingItem
    saved: GoogleListingItem | undefined
    venueName: string
    venueOptions: SingleSelectOption[]
    languages: Locale[]
    tickets: Record<string, ListedTicket>
    candidates: TicketCandidate[]
    candidatesLoading: boolean
    refusals: Record<string, string>
    markErrors: boolean
    canEdit: boolean
    onChange: (item: GoogleListingItem) => void
    /** Tells the page whether the listing passes every check, so Save can refuse to switch it on. */
    onReadinessChange: (ready: boolean) => void
    onDelete: () => void
    onImageUpload: (file: File) => Promise<any>
    replaceMessages: (id: string, status: MessageKind, text: string) => void
    removeAllMessages: () => void
}

const FIELD_ANCHORS: Record<ReadinessKey, string> = {
    place: 'gttd-field-place',
    categories: 'gttd-field-categories',
    description: 'gttd-field-description',
    image: 'gttd-field-image',
    tickets: 'gttd-field-tickets',
}

/** One venue's listing: the switch with its readiness gate, the venue form and the tickets. */
export function ListingCard(props: ListingCardProps) {
    const { t } = useTranslation()
    const { item, languages } = props
    const imageSize = useImageSize(item.image)
    const validity = useTicketValidity(
        props.articleService,
        props.accountSlug,
        item.options.map((option) => option.articleUUID),
    )
    const checks = readinessChecks({
        googlePlaceId: item.googlePlaceId,
        categories: item.options[0]?.category ?? [],
        descriptions: item.description,
        languages: languages.map((locale) => locale.code),
        image: item.image,
        imageSize,
        // A ticket whose price is not known yet (added, not saved) counts: backoffice refuses it on save if it has none.
        sellableTickets: item.options.filter((option) => props.tickets[option.articleUUID]?.price !== null).length,
    })
    const languageName = (code: string) => languages.find((locale) => locale.code === code)?.name ?? code
    checks.forEach((check) => {
        if (check.key === 'description' && check.detail) {
            check.detail = check.detail.split(', ').map(languageName).join(', ')
        }
    })
    const ready = isReady(checks)
    const failing = checks.filter((check) => !check.ok).length
    // The gate stops a listing from going on Google while a check fails. Switching a live
    // listing off and back on only restores what is saved, so that stays possible.
    const switchBlocked = !ready && !item.includeInFeed && !props.saved?.includeInFeed

    React.useEffect(() => {
        props.onReadinessChange(ready)
    }, [ready])

    const update = (patch: Partial<GoogleListingItem>) => props.onChange({ ...item, ...patch })

    const updateOption = (
        articleUuid: string,
        change: (option: GoogleListingItem['options'][number]) => GoogleListingItem['options'][number],
    ) =>
        update({
            options: item.options.map((option) => (option.articleUUID === articleUuid ? change(option) : option)),
        })

    /** Venue categories live on every option without its own set, so changing them updates each. */
    const onVenueCategoryChange = (categories: string[]) => {
        const venueCategories = item.options[0]?.category ?? []
        update({
            options: item.options.map((option) =>
                option.category.join() === venueCategories.join() ? { ...option, category: categories } : option,
            ),
        })
    }

    const onDescriptionChange = (text: string, language: string) => {
        const rest = item.description.filter((translation) => translation.language !== language)
        update({ description: [...rest, { language, text }] })
    }

    const onFix = (key: ReadinessKey) => {
        document.getElementById(FIELD_ANCHORS[key])?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }

    const onAddTicket = (articleUuid: string) =>
        update({
            options: [
                ...item.options,
                {
                    articleUUID: articleUuid,
                    category: item.options[0]?.category ?? [],
                    baseTicket: item.options.length === 0,
                    description: [],
                },
            ],
        })

    return (
        <section className="listing-card" aria-labelledby={`listing-${item.id}`}>
            <div className="listing-head">
                <div className="listing-head-text">
                    <h2 id={`listing-${item.id}`} className="listing-title">
                        {props.venueName || t('channels.googleTTD.venue.noVenue')}
                    </h2>
                    <StatusChip
                        tone={props.saved?.includeInFeed ? 'success' : 'neutral'}
                        label={t(
                            props.saved?.includeInFeed
                                ? 'channels.googleTTD.status.on'
                                : 'channels.googleTTD.status.off',
                        )}
                    />
                </div>
                <div className="listing-switch">
                    <DisabledItemHoverInfotip
                        active={switchBlocked}
                        infotipText={t('channels.googleTTD.listingBlocked', { count: failing })}
                        width="18em"
                    >
                        <Toggle
                            id={`listing-switch-${item.id}`}
                            isOn={item.includeInFeed}
                            onClick={(value) => update({ includeInFeed: value })}
                            disabled={!props.canEdit || switchBlocked}
                            className="smallInlineToggle"
                        />
                    </DisabledItemHoverInfotip>
                    <label htmlFor={`listing-switch-${item.id}`} className="listing-switch-label">
                        {t('channels.googleTTD.listingEnabled')}
                    </label>
                </div>
            </div>
            <ReadinessChecklist checks={checks} onFix={onFix} />
            <VenueSection
                item={item}
                venueName={props.venueName}
                venueOptions={props.venueOptions}
                languages={languages}
                imageSize={imageSize}
                markErrors={props.markErrors}
                canEdit={props.canEdit}
                onVenueChange={(locationId) => update({ locationId })}
                onPlaceIdChange={(googlePlaceId) => update({ googlePlaceId })}
                onCategoryChange={onVenueCategoryChange}
                onDescriptionChange={onDescriptionChange}
                onImageUpload={props.onImageUpload}
                onImageUploadSuccess={(response: ImageUploadResponse) => update({ image: response.url })}
                onRemoveImage={() => update({ image: '' })}
                replaceMessages={props.replaceMessages}
                removeAllMessages={props.removeAllMessages}
            />
            <div id="gttd-field-tickets">
                <TicketsTable
                    options={item.options}
                    tickets={props.tickets}
                    validity={validity}
                    candidates={props.candidates}
                    candidatesLoading={props.candidatesLoading}
                    venueCategories={item.options[0]?.category ?? []}
                    languages={languages}
                    canEdit={props.canEdit}
                    refusals={props.refusals}
                    onAdd={onAddTicket}
                    onRemove={(articleUuid) => {
                        const options = item.options.filter((option) => option.articleUUID !== articleUuid)
                        if (options.length > 0 && !options.some((option) => option.baseTicket)) {
                            options[0] = { ...options[0], baseTicket: true }
                        }
                        update({ options })
                    }}
                    onSetBase={(articleUuid) =>
                        update({
                            options: item.options.map((option) => ({
                                ...option,
                                baseTicket: option.articleUUID === articleUuid,
                            })),
                        })
                    }
                    onDescriptionChange={(articleUuid, text, language) =>
                        updateOption(articleUuid, (option) => ({
                            ...option,
                            description: [
                                ...(option.description ?? []).filter(
                                    (translation) => translation.language !== language,
                                ),
                                { language, text },
                            ],
                        }))
                    }
                    onCategoryChange={(articleUuid, categories) =>
                        updateOption(articleUuid, (option) => ({
                            ...option,
                            category: categories ?? item.options[0]?.category ?? [],
                        }))
                    }
                />
            </div>
            {props.canEdit && props.venueOptions.length > 1 && (
                <div className="listing-foot">
                    <button type="button" className="listing-delete" onClick={props.onDelete}>
                        {t('channels.googleTTD.deleteListing')}
                    </button>
                </div>
            )}
        </section>
    )
}
